import { useEffect, useMemo, useState, type FormEvent } from "react";
import { api } from "@/lib/api";
import { brandColors as colors } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import { dateSortValue, formatDate, formatInteger } from "@/lib/format";
import {
  AdminTableControls,
  AdminTableFilterSelect,
  SortableTh,
  useAdminTable,
} from "@/components/admin/AdminTableControls";
import AdminFormHeader from "@/components/admin/AdminFormHeader";

type Platform = "youtube" | "instagram" | "tiktok" | "other";

type Video = {
  id: string;
  title: string;
  platform: Platform;
  externalUrl: string;
  thumbnailUrl: string | null;
  description: string | null;
  publishedAt: string | null; // ISO
  viewCount: number;
  featured: boolean;
  sortOrder: number;
};

type VideoSortKey = "title" | "platform" | "views" | "published" | "featured";

type FormState = {
  title: string;
  platform: Platform;
  externalUrl: string;
  thumbnailUrl: string;
  description: string;
  publishedAt: string; // yyyy-mm-dd
  viewCount: string;
  sortOrder: string;
  featured: boolean;
};

const empty: FormState = {
  title: "",
  platform: "youtube",
  externalUrl: "",
  thumbnailUrl: "",
  description: "",
  publishedAt: "",
  viewCount: "0",
  sortOrder: "0",
  featured: false,
};

function toDateInput(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
}

function toForm(v: Video): FormState {
  return {
    title: v.title,
    platform: v.platform,
    externalUrl: v.externalUrl,
    thumbnailUrl: v.thumbnailUrl ?? "",
    description: v.description ?? "",
    publishedAt: toDateInput(v.publishedAt),
    viewCount: v.viewCount.toString(),
    sortOrder: v.sortOrder.toString(),
    featured: v.featured,
  };
}

function videoSearchText(video: Video) {
  return [
    video.title,
    video.platform,
    video.externalUrl,
    video.description,
    video.featured ? "featured" : "",
    video.publishedAt ? formatDate(video.publishedAt) : "",
    formatInteger(video.viewCount),
  ]
    .filter(Boolean)
    .join(" ");
}

const videoSorters: Record<VideoSortKey, (row: Video) => string | number> = {
  title: (row) => row.title,
  platform: (row) => row.platform,
  views: (row) => row.viewCount,
  published: (row) => dateSortValue(row.publishedAt),
  featured: (row) => (row.featured ? 1 : 0),
};

export default function AdminVideos() {
  const [list, setList] = useState<Video[]>([]);
  const [editing, setEditing] = useState<Video | "new" | null>(null);
  const [form, setForm] = useState<FormState>(empty);
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState("");
  const [platformFilter, setPlatformFilter] = useState("all");
  const [featuredFilter, setFeaturedFilter] = useState("all");

  async function refresh() {
    const r = await api.get<{ videos: Video[] }>("/api/admin/videos");
    setList(r.videos);
  }

  useEffect(() => {
    refresh().finally(() => setLoaded(true));
  }, []);

  function startNew() {
    setForm(empty);
    setEditing("new");
    setErr(null);
  }
  function startEdit(v: Video) {
    setForm(toForm(v));
    setEditing(v);
    setErr(null);
  }

  async function sync() {
    setSyncing(true);
    setErr(null);
    setNotice(null);
    try {
      const r = await api.post<{ inserted: number; updated: number }>(
        "/api/admin/videos/sync",
      );
      await refresh();
      setNotice(`Synced from YouTube — ${r.inserted} new, ${r.updated} refreshed.`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Sync failed");
    } finally {
      setSyncing(false);
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    const payload = {
      title: form.title,
      platform: form.platform,
      externalUrl: form.externalUrl,
      thumbnailUrl: form.thumbnailUrl || null,
      description: form.description || null,
      publishedAt: form.publishedAt || null,
      viewCount: parseInt(form.viewCount || "0", 10),
      sortOrder: parseInt(form.sortOrder || "0", 10),
      featured: form.featured,
    };
    try {
      if (editing === "new") await api.post("/api/admin/videos", payload);
      else if (editing) await api.put(`/api/admin/videos/${editing.id}`, payload);
      await refresh();
      setEditing(null);
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function remove(v: Video) {
    if (!confirm(`Delete "${v.title}"? This cannot be undone.`)) return;
    await api.del(`/api/admin/videos/${v.id}`);
    await refresh();
  }

  const platformOptions = useMemo(
    () =>
      ["all", ...Array.from(new Set(list.map((video) => video.platform))).sort()].map((value) => ({
        value,
        label: value === "all" ? "All platforms" : value,
      })),
    [list],
  );
  const filters = useMemo(
    () => [
      (row: Video) => platformFilter === "all" || row.platform === platformFilter,
      (row: Video) =>
        featuredFilter === "all" ||
        (featuredFilter === "featured" && row.featured) ||
        (featuredFilter === "standard" && !row.featured),
    ],
    [featuredFilter, platformFilter],
  );
  const {
    rows: tableRows,
    sort,
    requestSort,
  } = useAdminTable<Video, VideoSortKey>({
    rows: list,
    search,
    initialSort: { key: "published", direction: "desc" },
    sorters: videoSorters,
    searchText: videoSearchText,
    filters,
  });

  return (
    <section>
      <style>{css}</style>
      <div className="hdr">
        <h1 className="aflt-admin-title">Videos</h1>
        {!editing && (
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" className="btn-ghost" onClick={sync} disabled={syncing}>
              {syncing ? "Syncing…" : "↻ Sync from YouTube"}
            </button>
            <button type="button" className="btn-woven" onClick={startNew}>
              + New video
            </button>
          </div>
        )}
      </div>

      {notice && !editing && <p className="notice">{notice}</p>}

      {editing ? (
        <form onSubmit={submit} className="paper-card">
          <AdminFormHeader
            title={editing === "new" ? "New video" : editing.title.slice(0, 72)}
            eyebrow={editing === "new" ? "Create video" : "Edit video"}
            backLabel="Back to videos"
            onBack={() => setEditing(null)}
          />
          <Field label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} required />
          <div className="grid2">
            <label className="fld">
              <span>Platform</span>
              <select
                value={form.platform}
                onChange={(e) => setForm({ ...form, platform: e.target.value as Platform })}
              >
                <option value="youtube">youtube</option>
                <option value="instagram">instagram</option>
                <option value="tiktok">tiktok</option>
                <option value="other">other</option>
              </select>
            </label>
            <Field
              label="Published (YYYY-MM-DD)"
              value={form.publishedAt}
              onChange={(v) => setForm({ ...form, publishedAt: v })}
            />
          </div>
          <Field
            label="Video URL"
            value={form.externalUrl}
            onChange={(v) => setForm({ ...form, externalUrl: v })}
            required
          />
          <Field
            label="Thumbnail URL"
            value={form.thumbnailUrl}
            onChange={(v) => setForm({ ...form, thumbnailUrl: v })}
          />
          <Field
            label="Description"
            value={form.description}
            onChange={(v) => setForm({ ...form, description: v })}
            textarea
          />
          <div className="grid2">
            <Field label="View count" value={form.viewCount} onChange={(v) => setForm({ ...form, viewCount: v })} />
            <Field label="Sort order" value={form.sortOrder} onChange={(v) => setForm({ ...form, sortOrder: v })} />
          </div>
          <label className="fld checkbox">
            <input
              type="checkbox"
              checked={form.featured}
              onChange={(e) => setForm({ ...form, featured: e.target.checked })}
            />
            <span style={{ marginLeft: 8 }}>Featured (shows in the homepage “most viewed” rail)</span>
          </label>
          {err && <p className="err">{err}</p>}
          <div className="actions">
            <button type="submit" className="btn-woven" disabled={busy}>
              {busy ? "Saving…" : "Save"}
            </button>
            <button type="button" className="btn-ghost" onClick={() => setEditing(null)}>
              Cancel
            </button>
          </div>
          <p className="note">
            “Sync from YouTube” refreshes view counts and pulls new uploads. It never overwrites
            titles, featured, or sort order you set here.
          </p>
        </form>
      ) : !loaded ? (
        <p style={{ fontFamily: fonts.body, color: `${colors.navy}aa` }}>Loading…</p>
      ) : (
        <>
          <AdminTableControls
            search={search}
            onSearchChange={setSearch}
            placeholder="Search videos, URLs, descriptions..."
            totalCount={list.length}
            resultCount={tableRows.length}
          >
            <AdminTableFilterSelect
              label="Platform"
              value={platformFilter}
              onChange={setPlatformFilter}
              options={platformOptions}
            />
            <AdminTableFilterSelect
              label="Featured"
              value={featuredFilter}
              onChange={setFeaturedFilter}
              options={[
                { value: "all", label: "All videos" },
                { value: "featured", label: "Featured" },
                { value: "standard", label: "Not featured" },
              ]}
            />
          </AdminTableControls>
          {list.length === 0 ? (
            <p style={{ fontFamily: fonts.body, color: `${colors.navy}aa` }}>
              No videos yet. Use “Sync from YouTube” to pull her latest, or add one manually.
            </p>
          ) : tableRows.length === 0 ? (
            <p style={{ fontFamily: fonts.body, color: `${colors.navy}aa` }}>No videos match those filters.</p>
          ) : (
            <table className="paper-table">
              <thead>
                <tr>
                  <th />
                  <SortableTh label="Title" sortKey="title" sort={sort} onSort={requestSort} />
                  <SortableTh label="Platform" sortKey="platform" sort={sort} onSort={requestSort} />
                  <SortableTh label="Views" sortKey="views" sort={sort} onSort={requestSort} />
                  <SortableTh label="Published" sortKey="published" sort={sort} onSort={requestSort} />
                  <SortableTh label="Featured" sortKey="featured" sort={sort} onSort={requestSort} />
                  <th />
                </tr>
              </thead>
              <tbody>
                {tableRows.map((v) => (
                  <tr key={v.id}>
                    <td style={{ width: 72 }}>
                      {v.thumbnailUrl ? (
                        <img className="thumb" src={v.thumbnailUrl} alt="" />
                      ) : (
                        <div className="thumb thumb-empty" />
                      )}
                    </td>
                    <td>
                      <strong>{v.title.slice(0, 70)}</strong>
                      <a className="slug" href={v.externalUrl} target="_blank" rel="noopener noreferrer">
                        {v.externalUrl.replace(/^https?:\/\/(www\.)?/, "")}
                      </a>
                    </td>
                    <td>{v.platform}</td>
                    <td>{formatInteger(v.viewCount)}</td>
                    <td>{v.publishedAt ? formatDate(v.publishedAt) : "-"}</td>
                    <td>{v.featured ? "Yes" : ""}</td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      <button type="button" className="btn-ghost sm" onClick={() => startEdit(v)}>
                        Edit
                      </button>
                      <button type="button" className="btn-danger sm" onClick={() => remove(v)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </section>
  );
}

function Field({
  label,
  value,
  onChange,
  required,
  textarea,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  textarea?: boolean;
}) {
  return (
    <label className="fld">
      <span>{label}</span>
      {textarea ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} required={required} />
      ) : (
        <input type="text" value={value} onChange={(e) => onChange(e.target.value)} required={required} />
      )}
    </label>
  );
}

const css = `
  .hdr { display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin-bottom: 28px; }
  .btn-woven {
    display: inline-flex; align-items: center; gap: 7px; padding: 10px 20px;
    background: ${colors.berryRose}; color: #fff; font-family: ${fonts.body};
    font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
    border: none; border-radius: 7px; cursor: pointer;
    outline: 2px dashed rgba(255,255,255,0.45); outline-offset: -5px; box-shadow: 0 2px 8px rgba(212,96,106,0.30);
  }
  .btn-woven:hover { background: #c0535d; }
  .btn-woven:disabled { opacity: 0.6; cursor: default; }
  .btn-ghost {
    background: ${colors.cream}; border: 1px solid rgba(201,147,76,0.55); color: ${colors.navy};
    font-family: ${fonts.body}; font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
    border-radius: 7px; padding: 9px 16px; cursor: pointer;
  }
  .btn-ghost:hover { box-shadow: 0 6px 14px -8px rgba(8,45,61,0.35); }
  .btn-ghost:disabled { opacity: 0.6; cursor: default; }
  .btn-danger { background: ${colors.cream}; border: 1px solid ${colors.berryRose}; color: ${colors.berryRose};
    font-family: ${fonts.body}; font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
    border-radius: 7px; padding: 9px 16px; cursor: pointer; }
  .btn-danger:hover { background: ${colors.berryRose}; color: #fff; }
  .sm { padding: 6px 12px; margin-left: 6px; font-size: 0.62rem; }

  .notice { font-family: ${fonts.body}; font-size: 0.82rem; font-weight: 600; color: #2e7d52;
    background: #2e7d5214; border: 1px solid #2e7d5233; border-radius: 10px; padding: 0.6rem 0.9rem; margin-bottom: 1rem; }

  .paper-table { width: 100%; border-collapse: separate; border-spacing: 0; background: #fff; border: 1.5px dashed rgba(201,147,76,0.40); border-radius: 16px; overflow: hidden; }
  .paper-table th, .paper-table td { text-align: left; padding: 0.7rem 0.9rem; border-bottom: 1px dashed rgba(201,147,76,0.18); font-family: ${fonts.body}; vertical-align: middle; }
  .paper-table thead th { background: ${colors.blushLight}; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.18em; color: rgba(8,45,61,0.7); border-bottom: 1px solid rgba(201,147,76,0.15); }
  .paper-table tbody tr:last-child td { border-bottom: none; }
  .paper-table tbody td { font-size: 13.5px; color: rgba(8,45,61,0.85); }
  .paper-table .slug { display: block; font-size: 11px; color: rgba(8,45,61,0.47); text-decoration: none; }
  .paper-table .slug:hover { color: ${colors.berryRose}; text-decoration: underline; }
  .thumb { width: 64px; height: 36px; object-fit: cover; border-radius: 5px; display: block; }
  .thumb-empty { background: ${colors.blushLight}; }

  .paper-card { background: #fff; border: 1.5px dashed rgba(201,147,76,0.40); border-radius: 16px; padding: 1.5rem 1.75rem; }
  .form-legend { font-family: ${fonts.logo}; font-weight: 500; font-size: 1.2rem; color: ${colors.navy}; display: block; margin-bottom: 1.25rem; }
  .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
  .fld { display: block; margin-bottom: 1rem; }
  .fld > span { display: block; font-family: ${fonts.body}; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; color: rgba(8,45,61,0.65); margin-bottom: 0.35rem; }
  .fld input[type=text], .fld select {
    width: 100%; padding: 0.55rem 0.9rem; border: 1px solid rgba(201,147,76,0.40); border-radius: 999px;
    background: ${colors.cream}; color: ${colors.navy}; font-family: ${fonts.body}; font-weight: 600; font-size: 13.5px; outline: none;
  }
  .fld textarea {
    width: 100%; min-height: 80px; padding: 0.7rem 0.9rem; border: 1px solid rgba(201,147,76,0.40); border-radius: 12px;
    background: ${colors.cream}; color: ${colors.navy}; font-family: ${fonts.body}; font-weight: 500; font-size: 13.5px; outline: none;
  }
  .fld input:focus, .fld select:focus, .fld textarea:focus { border-color: ${colors.berryRose}; box-shadow: 0 4px 14px -8px rgba(212,96,106,0.55); }
  .fld.checkbox { display: flex; align-items: center; }
  .fld.checkbox input { accent-color: ${colors.berryRose}; }
  .actions { display: flex; gap: 0.6rem; margin-top: 0.5rem; }
  .err { color: ${colors.berryRose}; font-family: ${fonts.body}; font-weight: 600; font-size: 0.85rem; margin: 0.5rem 0; }
  .note { font-family: ${fonts.body}; font-size: 0.75rem; color: rgba(8,45,61,0.5); margin-top: 0.75rem; }
`;
